/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { PureComponent } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import PermissionDenied from 'views/components/permission-denied';
import PrivateRoute from 'views/components/private-route';
import PublicRoute from 'views/components/public-route';
import { batch } from 'react-redux';
import equal from 'fast-deep-equal';
import { getAbility } from 'modules/auth/selectors';
import { getMeItem } from 'modules/me/selectors';
import { isAllowed } from 'utils/casl/ability-utils';

// Whether the signed-in user's role fails the one a route demands.
//
// A missing `me` counts as denied, mirroring how isPermissionDenied treats a missing
// ability. Both arrive together — the auth sagas put `retrieveMe.success` before the
// `verifySession.SUCCESS`/`loginUser.SUCCESS` that flips `isLoggedIn`, and the /app
// PrivateRoute renders no child until then — so absence is not evidence of a role.
const isRoleDenied = (store, requiredRole) => {
  if (!requiredRole) return false;
  const me = getMeItem(store.getState());
  return me?.role?.name !== requiredRole;
};

// Whether the signed-in user lacks the CASL permissions a route declares.
//
// Being denied has to stop the route's `actions` as well as its screen: those calls come
// back 401, and the SDK interceptor cannot tell a permission 401 apart from an expired
// session, so it signs the user out and PrivateRoute hands them to the accounts app —
// to fix a problem that signing in again cannot fix.
//
// A missing ability counts as denied. Privated routes only mount after verifySession,
// which always writes one, so its absence is not evidence of permission.
//
// Read at mount and at render, from the store rather than a subscription (as with
// isRoleDenied): permissions arrive with the session, before any privated screen mounts.
const isPermissionDenied = (store, requiredPermissions) => {
  if (!requiredPermissions) return false;
  const ability = getAbility(store.getState());
  return !ability || !isAllowed(requiredPermissions, ability);
};

const isRouteDenied = ({ store, requiredRole, requiredPermissions }) =>
  isRoleDenied(store, requiredRole) ||
  isPermissionDenied(store, requiredPermissions);

class RouteWithSubRoutes extends PureComponent {
  componentDidMount() {
    const {
      actions,
      store,
      computedMatch: { params },
    } = this.props;

    if (isRouteDenied(this.props)) return;

    if (actions) {
      batch(() => {
        actions.forEach((action) => {
          store.dispatch(action(params));
        });
      });
    }
  }

  componentDidUpdate(prevProps) {
    const {
      actions,
      actionsDidUpdate,
      store,
      computedMatch: { params },
    } = this.props;
    const {
      computedMatch: { params: prevParams },
    } = prevProps;

    if (isRouteDenied(this.props)) return;

    if (!equal(prevParams, params)) {
      if (actionsDidUpdate) {
        batch(() => {
          actionsDidUpdate.forEach((action) => {
            store.dispatch(action(params));
          });
        });
      }
      if (actions) {
        batch(() => {
          actions.forEach((action) => {
            store.dispatch(action(params));
          });
        });
      }
    }
  }

  componentWillUnmount() {
    const {
      actionsUnMount,
      store: { dispatch },
      computedMatch: { params },
    } = this.props;

    if (actionsUnMount) {
      batch(() => {
        actionsUnMount.forEach((action) => {
          dispatch(action(params));
        });
      });
    }
  }

  render() {
    const {
      component: Component,
      exact,
      path,
      routes,
      privated,
      published,
      requiredRole,
      requiredPermissions,
      store,
      ...rest
    } = this.props;

    let RouterFinal = Route;
    if (privated) {
      RouterFinal = PrivateRoute;
    }
    if (published) {
      RouterFinal = PublicRoute;
    }

    // Enforced here and not only in PrivateRoute: `privated` is declared once, on the
    // /app parent, so every child renders through a plain Route that drops the
    // `requiredRole` prop. Checked at render for the same reason as the permission
    // gate below — suppressing the route's actions leaves the screen itself readable.
    if (isRoleDenied(store, requiredRole)) {
      return (
        <RouterFinal
          exact={exact}
          path={path}
          render={() => <Redirect to="/app" />}
        />
      );
    }

    // Still routed through RouterFinal so the path has to match first — a page the user
    // may not see should refuse them, not shadow whatever route comes next.
    if (isPermissionDenied(store, requiredPermissions)) {
      return (
        <RouterFinal
          exact={exact}
          path={path}
          render={() => <PermissionDenied />}
        />
      );
    }

    if (routes) {
      return (
        <RouterFinal
          exact={exact}
          path={path}
          requiredRole={requiredRole}
          render={(routeProps) => (
            <Component {...routeProps} {...rest}>
              <Switch>
                {routes.map((route) => (
                  <RouteWithSubRoutes
                    key={route.path}
                    store={store}
                    {...route}
                  />
                ))}
              </Switch>
            </Component>
          )}
        />
      );
    }

    return (
      <RouterFinal
        exact={exact}
        path={path}
        requiredRole={requiredRole}
        render={(routeProps) => <Component {...routeProps} {...rest} />}
      />
    );
  }
}

const RouteConfig = ({ routes, store }) => (
  <Switch>
    {routes.map((route) => (
      <RouteWithSubRoutes key={route.path} store={store} {...route} />
    ))}
  </Switch>
);

export default RouteConfig;
